import { CARRYFORWARD_FLOOR_ISO } from '@utazzo/shared';
import React from 'react';

import { LegacyTable, ReportSection, type LegacyCell } from '../ReportSection';

import {
  contactabilityToActive,
  ghostRatios,
  num,
  pct,
  queryToActive,
  ratioCell,
} from './band-helpers';

import type { BandProps } from './band-props';
import type { AgentTrackerRow, LeadListQuery, PerformanceDayRow } from '@utazzo/shared';

import { istIsoDate } from '@/features/leads/lead-date-range';

// ----- Performance Insights: every Lead(s)/Attempted/Delta number drills to
// the exact leads behind it. Each cohort maps to a lead-list filter set that
// reproduces how the tracker counted it, so the popup total matches the cell.
// `agentId` null = the Total row (whole visible scope). The non-Fresh cohorts
// are WHOLE-POOL (current month + carry forward): `createdAtFrom = the
// carry-forward floor` + current-state predicate, matching `row.combinedPool`.
type PerfCohort = 'fresh' | 'notContacted' | 'revertPending' | 'dueToday' | 'overdue';
type PerfKind = 'total' | 'attempted' | 'delta';

export const PerformanceInsightsBand = ({
  report,
  totals,
  loading,
  agentEntries,
  agentCell,
  expandAllAction,
  drillCell,
  from,
  to,
  perfDay,
  istToday,
  isPerfToday,
  setPerfDay,
  perfDayData,
  perfDayLoading,
}: BandProps): React.JSX.Element => {
  const perfQuery = (
    agentId: string | null,
    cohort: PerfCohort,
    kind: PerfKind,
  ): Partial<LeadListQuery> => {
    const base: Partial<LeadListQuery> = agentId ? { assignedAgentId: agentId } : {};
    // Revert pending is special: the work is SENDING the itinerary (not a call),
    // and a sent lead drops out of `isRevertPending`. So the three cells map to
    // the reconstructed `revertPendingState` legs, not the call `attemptedToday`.
    if (cohort === 'revertPending') {
      const state = kind === 'attempted' ? 'reverted_today' : kind === 'delta' ? 'pending' : 'any';
      return {
        ...base,
        revertPendingState: state,
        currentBucket: 'OPEN',
        createdAtFrom: CARRYFORWARD_FLOOR_ISO,
      };
    }
    const cohortQ: Partial<LeadListQuery> =
      cohort === 'fresh'
        ? // IST calendar day (resets at IST midnight), matching effort.fresh on
          // the server and the inbox's "Today's Fresh Leads" tile - not a
          // rolling 24h window.
          { createdAtFrom: istIsoDate(), createdAtTo: istIsoDate() }
        : cohort === 'notContacted'
          ? {
              requiresFirstContact: true,
              currentBucket: 'OPEN',
              createdAtFrom: CARRYFORWARD_FLOOR_ISO,
            }
          : cohort === 'dueToday'
            ? {
                followupState: 'DUE_TODAY',
                currentBucket: 'OPEN',
                createdAtFrom: CARRYFORWARD_FLOOR_ISO,
              }
            : {
                followupState: 'OVERDUE',
                currentBucket: 'OPEN',
                createdAtFrom: CARRYFORWARD_FLOOR_ISO,
              };
    const kindQ: Partial<LeadListQuery> =
      kind === 'attempted'
        ? { attemptedToday: true }
        : kind === 'delta'
          ? { attemptedToday: false }
          : {};
    return { ...base, ...cohortQ, ...kindQ };
  };
  const perfCell = (
    value: number,
    agentId: string | null,
    title: string,
    cohort: PerfCohort,
    kind: PerfKind,
  ): LegacyCell => drillCell(value, value, value, title, perfQuery(agentId, cohort, kind));
  // The 15 numeric cells for one Performance row (5 cohorts x Lead(s)/Attempted/
  // Delta). Fresh stays month-only; the rest read the combined pool.
  const perfRow = (r: AgentTrackerRow): LegacyCell[] => {
    const id = (r.agentId as string | null) ?? null;
    const cp = r.combinedPool;
    // Total group = sum of every category's Lead(s)/Attempted (and the derived
    // pending Delta). A lead in several categories is counted once per category,
    // so this is the total WORK across buckets, not a distinct lead count.
    const totLeads = r.effort.fresh + cp.notContacted + cp.revertPending + cp.dueToday + cp.overdue;
    const totAttempted =
      r.effort.freshAttempted +
      cp.notContactedAttempted +
      cp.revertPendingAttempted +
      cp.dueTodayAttempted +
      cp.overdueAttempted;
    return [
      perfCell(r.effort.fresh, id, 'Fresh lead(s)', 'fresh', 'total'),
      perfCell(r.effort.freshAttempted, id, 'Fresh - attempted', 'fresh', 'attempted'),
      perfCell(r.effort.fresh - r.effort.freshAttempted, id, 'Fresh - pending', 'fresh', 'delta'),
      perfCell(cp.notContacted, id, 'Not contacted', 'notContacted', 'total'),
      perfCell(
        cp.notContactedAttempted,
        id,
        'Not contacted - attempted',
        'notContacted',
        'attempted',
      ),
      perfCell(
        cp.notContacted - cp.notContactedAttempted,
        id,
        'Not contacted - pending',
        'notContacted',
        'delta',
      ),
      perfCell(cp.revertPending, id, 'Revert pending', 'revertPending', 'total'),
      perfCell(
        cp.revertPendingAttempted,
        id,
        'Revert pending - attempted',
        'revertPending',
        'attempted',
      ),
      perfCell(
        cp.revertPending - cp.revertPendingAttempted,
        id,
        'Revert pending - pending',
        'revertPending',
        'delta',
      ),
      perfCell(cp.dueToday, id, 'Due today follow-up(s)', 'dueToday', 'total'),
      perfCell(cp.dueTodayAttempted, id, 'Due today - attempted', 'dueToday', 'attempted'),
      perfCell(cp.dueToday - cp.dueTodayAttempted, id, 'Due today - pending', 'dueToday', 'delta'),
      perfCell(cp.overdue, id, 'Missed follow-up(s)', 'overdue', 'total'),
      perfCell(cp.overdueAttempted, id, 'Missed - attempted', 'overdue', 'attempted'),
      perfCell(cp.overdue - cp.overdueAttempted, id, 'Missed - pending', 'overdue', 'delta'),
      // Total group (aggregate - not clickable).
      { node: num(totLeads), sort: totLeads },
      { node: num(totAttempted), sort: totAttempted },
      { node: num(totLeads - totAttempted), sort: totLeads - totAttempted },
    ];
  };
  // TTV-to-budget variance cells drill to the leads in that variance bucket,
  // scoped to the report month + agent (the server resolves the exact ids).
  const budgetCell = (
    value: number,
    agentId: string | null,
    bucket: 'below' | 'upTo5' | 'upTo10' | 'moreThan10',
    title: string,
  ): LegacyCell =>
    drillCell(value, value, value, title, {
      ...(agentId ? { assignedAgentId: agentId } : {}),
      budgetVarianceBucket: bucket,
      createdAtFrom: from,
      createdAtTo: to,
    });
  const budgetCells = (r: AgentTrackerRow): LegacyCell[] => {
    const id = (r.agentId as string | null) ?? null;
    return [
      budgetCell(r.budgetVariance.belowBudget, id, 'below', 'Below budget (TTV under budget)'),
      budgetCell(r.budgetVariance.upTo5, id, 'upTo5', 'Within 5% of budget'),
      budgetCell(r.budgetVariance.upTo10, id, 'upTo10', '5-10% over budget'),
      budgetCell(r.budgetVariance.moreThan10, id, 'moreThan10', 'More than 10% over budget'),
    ];
  };

  // Performance Insights day picker (day-wise follow-up productivity). Native
  // date input, capped at today (IST) - past days only for the reconstructed
  // view; today falls back to the live snapshot table.
  const perfDayPicker = (
    <label className="flex items-center gap-2 text-xs font-normal">
      <span className="text-muted-foreground whitespace-nowrap normal-case">Day</span>
      <input
        type="date"
        value={perfDay}
        max={istToday}
        onChange={(e) => setPerfDay(e.target.value)}
        className="border-border bg-background text-foreground h-8 rounded-md border px-2 text-sm"
      />
      {!isPerfToday && (
        <button
          type="button"
          onClick={() => setPerfDay(istToday)}
          className="text-primary underline-offset-2 hover:underline"
        >
          Today
        </button>
      )}
    </label>
  );
  // The reconstructed day-view table (only rendered for a PAST day). Rows come
  // from the dedicated performance-day endpoint, sorted by agent name server-side.
  const perfDayNum = (n: number): LegacyCell => ({ node: num(n), sort: n });
  const perfDayRow = (r: PerformanceDayRow): LegacyCell[] => [
    { node: r.agentName, sort: r.agentName },
    perfDayNum(r.freshLeads),
    perfDayNum(r.freshTouched),
    // Delta = of that day's fresh leads, how many were NOT touched (pending).
    perfDayNum(r.freshLeads - r.freshTouched),
    perfDayNum(r.dueFollowUps),
    perfDayNum(r.dueCompleted),
    // Delta = of that day's due follow-ups, how many were NOT completed.
    perfDayNum(r.dueFollowUps - r.dueCompleted),
    perfDayNum(r.missedFollowUps),
    perfDayNum(r.attemptsLogged),
    perfDayNum(r.leadsTouched),
  ];
  const performanceDayTable = (
    <LegacyTable
      loading={perfDayLoading || !perfDayData}
      emptyText="No follow-up activity on this day."
      headerGroups={[
        { label: '', colSpan: 1 },
        { label: 'Fresh Lead(s)', colSpan: 3 },
        { label: 'Follow-ups Due', colSpan: 3 },
        { label: 'Missed', colSpan: 1 },
        { label: 'Attempts Logged', colSpan: 2 },
      ]}
      headers={[
        'Agent',
        'Lead(s)',
        'Touched',
        'Delta',
        'Due',
        'Completed',
        'Delta',
        'As of day',
        'Follow-ups',
        'Leads',
      ]}
      rows={(perfDayData?.rows ?? []).map((r) => perfDayRow(r))}
      totals={perfDayData ? perfDayRow(perfDayData.totals) : undefined}
    />
  );

  return (
    <div className="space-y-4 pt-2">
      {/* The Ghosting KPI card now lives in the Performance band's loss row; this
          band keeps only its tables (Performance Insights + Contactability,
          Ghosting & Budget). */}
      <ReportSection
        title={
          isPerfToday
            ? 'Performance Insights'
            : `Performance Insights - ${new Date(`${perfDay}T00:00:00`).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })}`
        }
        actions={
          <>
            {perfDayPicker}
            {isPerfToday && expandAllAction}
          </>
        }
        toolbar={
          isPerfToday ? undefined : (
            <p className="text-muted-foreground text-xs">
              Day-wise follow-up productivity, rebuilt from timestamps. Fresh = leads created that
              day (Touched = first follow-up logged same day); Follow-ups Due = due that day and how
              many completed; Missed = follow-ups due by that day still open at day-end; Attempts
              Logged = follow-ups completed that day. Live-only backlog cohorts (Not Contacted /
              Revert Pending) are shown for today only.
            </p>
          )
        }
      >
        {isPerfToday ? (
          <LegacyTable
            loading={loading || !report}
            headerGroups={[
              { label: '', colSpan: 1 },
              { label: 'Fresh Lead(s)', colSpan: 3 },
              { label: 'Not Contacted', colSpan: 3 },
              { label: 'Revert Pending', colSpan: 3 },
              { label: "Today's Follow Up(s)", colSpan: 3 },
              { label: 'Missed Follow Up(s)', colSpan: 3 },
              { label: 'Total', colSpan: 3 },
            ]}
            headers={[
              'Agent',
              'Lead(s)',
              'Attempted',
              'Delta',
              'Lead(s)',
              'Attempted',
              'Delta',
              'Lead(s)',
              'Attempted',
              'Delta',
              'Lead(s)',
              'Attempted',
              'Delta',
              'Lead(s)',
              'Attempted',
              'Delta',
              'Lead(s)',
              'Attempted',
              'Delta',
            ]}
            rows={agentEntries.map(({ row, ...entry }) => [
              { node: agentCell({ row, ...entry }), sort: row.agentName },
              ...perfRow(row),
            ])}
            totals={totals && ['Total', ...perfRow(totals)]}
          />
        ) : (
          performanceDayTable
        )}
      </ReportSection>

      <ReportSection title="Contactability, Ghosting & Budget" actions={expandAllAction}>
        <LegacyTable
          loading={loading || !report}
          headerGroups={[
            { label: '', colSpan: 1 },
            { label: 'Contactability', colSpan: 5 },
            { label: 'Ghosting (Client Unresponsive)', colSpan: 4 },
            { label: 'TTV to Budget Miss Match', colSpan: 4 },
          ]}
          headers={[
            'Agent',
            'Contacted (n)',
            'Reached Active (n)',
            'Contactability (%)',
            'Query to Active (%)',
            'Contactability to Active (%)',
            'From Proposal (%)',
            'From Warm (%)',
            'From Hot (%)',
            'Total to Active (%)',
            'Below Budget',
            '5% Variance',
            '10% Variance',
            'Greater than 10% Variance',
          ]}
          rows={agentEntries.map(({ row, ...entry }) => [
            { node: agentCell({ row, ...entry }), sort: row.agentName },
            num(row.contactedCount),
            num(row.reachedActive),
            pct(row.contactabilityPct),
            queryToActive(row),
            contactabilityToActive(row),
            ratioCell(ghostRatios(row).proposal),
            ratioCell(ghostRatios(row).warm),
            ratioCell(ghostRatios(row).hot),
            ratioCell(ghostRatios(row).total),
            ...budgetCells(row),
          ])}
          totals={
            totals && [
              'Total',
              num(totals.contactedCount),
              num(totals.reachedActive),
              pct(totals.contactabilityPct),
              queryToActive(totals),
              contactabilityToActive(totals),
              ratioCell(ghostRatios(totals).proposal),
              ratioCell(ghostRatios(totals).warm),
              ratioCell(ghostRatios(totals).hot),
              ratioCell(ghostRatios(totals).total),
              ...budgetCells(totals),
            ]
          }
        />
      </ReportSection>
    </div>
  );
};
